import React, { useEffect, useRef, useState } from "react";

/* ------------------------------------------------------------
 * Config
 * ---------------------------------------------------------- */
const API_BASE_URL = process.env.REACT_APP_API_URL || "";
const WS_BASE_URL  = (process.env.REACT_APP_WS_URL || "").replace(/^http/, "ws");

const getAuthHeaders = () => {
  const tok = localStorage.getItem("access_token") || "";
  return { "Content-Type": "application/json", Authorization: `Bearer ${tok}` };
};

/* ------------------------------------------------------------
 * Utils
 * ---------------------------------------------------------- */
// ⚙️  sostituisci l’utility nel tuo componente
const groupByDish = (rows = []) => {
  const map = new Map();

  rows.forEach((r) => {
    // id robusto: se manca menu_item_id ripiega su name
    const key = r.menu_item_id ?? r.menuItemId ?? r.name;
    if (key == null) return;                   // scarta righe senza chiave

    const price  = Number(r.menu_item_price ?? r.price ?? 0);
    const qty    = Number(r.quantity ?? r.qty ?? 0);

    const cur = map.get(key) || {
      menu_item_id   : r.menu_item_id ?? null,
      menu_item_name : r.menu_item_name ?? r.name,
      menu_item_price: price,
      quantity       : 0,
    };

    cur.quantity += qty;

    /* se arrivano versioni dello stesso piatto con prezzi diversi
       mantieni il più alto, così il totale non viene mai sottostimato */
    if (price > cur.menu_item_price) cur.menu_item_price = price;

    map.set(key, cur);
  });

  return [...map.values()];
};


/* ===================================================================
 * COMPONENT
 * =================================================================*/
export default function OpenTablesView({ selectedRestaurant }) {
  /* ----------------------------- state --------------------------- */
  const [tables,           setTables]            = useState([]);
  const [error,            setError]             = useState(null);

  const [expanded,         setExpanded]          = useState({});
  const [sessionByTable,   setSessionByTable]    = useState({});
  const [sessionToTable,   setSessionToTable]    = useState({});
  const [fullItemsBySess,  setFullItemsBySess]   = useState({});
  const [summaryBySess,    setSummaryBySess]     = useState({});
  const [finalized,        setFinalized]         = useState({});

  const [modalOpen,        setModalOpen]         = useState(false);
  const [editingSessionId, setEditingSessionId]  = useState(null);
  const [saving,           setSaving]            = useState(false);

  /* refs ---------------------------------------------------------- */
  const wsRef              = useRef(null);
  const editingSessionRef  = useRef(null);
  const tablesRef          = useRef([]);
  const expandedRef        = useRef({});

  useEffect(() => { editingSessionRef.current = editingSessionId; });
  useEffect(() => { tablesRef.current   = tables;          }, [tables]);
  useEffect(() => { expandedRef.current = expanded;        }, [expanded]);

  /* ---------------------------------------------------------------
   * carica i tavoli occupati (endpoint ottimizzato)
   * ------------------------------------------------------------- */
  const loadTables = async () => {
    try {
      setError(null);

      let url = `${API_BASE_URL}/open_tables`;
      if (selectedRestaurant && selectedRestaurant !== "all") {
        url += `?restaurant_id=${selectedRestaurant}`;
      }

      const r = await fetch(url, { headers: getAuthHeaders() });
      if (!r.ok) throw new Error(r.status);

      const rows = await r.json();                        // dati già raggruppati dal backend

      // adattiamo la struttura per la UI pre-esistente
      const newTables = rows.map((row) => ({
        id            : row.table_id,
        table_number  : row.table_number,
        restaurant_id : row.restaurant_id,
        status        : "occupied",                       // per compatibilità UI
        raw           : row,
      }));
      setTables(newTables);

      // mappe helper
      const mapTblSess = {};
      const mapSessTbl = {};
      const mapSummary = {};
      const mapFin     = {};

      rows.forEach((row) => {
        mapTblSess[row.table_id]       = row.session_id;
        mapSessTbl[row.session_id]     = { id: row.table_id, table_number: row.table_number };
        mapFin[row.session_id]         = row.finalized;

        // ri-raggruppiamo per sicurezza
        const deliveredRows = (row.delivered_items || []).map((d) => ({
          menu_item_id   : d.menu_item_id,
          menu_item_name : d.name,
          menu_item_price: d.price,
          quantity       : d.qty,
        }));
        mapSummary[row.session_id] = groupByDish(deliveredRows);
      });
      setSessionByTable(mapTblSess);
      setSessionToTable(mapSessTbl);
      setSummaryBySess(mapSummary);
      setFinalized(mapFin);

      // se avevo pannelli aperti li aggiorno
      refreshExpandedSessions(newTables);
    } catch (e) {
      console.error(e);
      setError("Impossibile caricare i tavoli");
    }
  };

  /* ---------------------------------------------------------------
   * carica dettagli sessione (ordini + items NON aggregati)
   * ------------------------------------------------------------- */
  const loadSessionData = async (tableOrRow) => {
    const tableId = tableOrRow.id ?? tableOrRow.table_id;
    const sessionId = sessionByTable[tableId] || tableOrRow.raw?.session_id;
    if (!sessionId) return;

    try {
      const oRes = await fetch(
        `${API_BASE_URL}/orders?table_session_id=${sessionId}`,
        { headers: getAuthHeaders() }
      );
      if (!oRes.ok) throw new Error(oRes.status);
      const oJson  = await oRes.json();
      const orders = Array.isArray(oJson) ? oJson : oJson.orders || [];

      const items = [];
      orders.forEach((o) =>
        o.items?.forEach((it) =>
          items.push({
            ...it,
            order_id       : o.id,
            menu_item_price: it.menu_item_price || 0,
          })
        )
      );
      setFullItemsBySess((p) => ({ ...p, [sessionId]: items }));
    } catch (e) {
      console.error(e);
      setError("Errore caricamento dettagli ordini");
    }
  };

  /* ---------------------------------------------------------------
   * PATCH: decrementa qty (usa il nuovo endpoint)
   * ------------------------------------------------------------- */
  const decrementItem = (itemId) =>
    fetch(`${API_BASE_URL}/order_items/${itemId}/decrement`, {
      method : "PATCH",
      headers: getAuthHeaders(),
    });

  const handleMinus = async (it) => {
    if (saving || it.quantity === 0) return;
    setSaving(true);
    try {
      const resp = await (await decrementItem(it.id)).json();

      // aggiorna riepilogo **server side** (payload già aggregato)
      const sid = resp.session_id;
      const delRows = resp.delivered_items.map((d) => ({
        menu_item_id   : d.menu_item_id,
        menu_item_name : d.name,
        menu_item_price: d.price,
        quantity       : d.qty,
      }));
      setSummaryBySess((prev) => ({ ...prev, [sid]: groupByDish(delRows) }));

      // ricarica lista completa per la modale
      await refreshEditingSession();
    } catch (e) {
      console.error(e);
    }
    setSaving(false);
  };

  const refreshEditingSession = async () => {
    const table = sessionToTable[editingSessionRef.current];
    if (table) await loadSessionData(table);
  };

  /* ---------------------------------------------------------------
   * helper per ricaricare pannelli già aperti
   * ------------------------------------------------------------- */
  const refreshExpandedSessions = (tbls) => {
    tbls.filter((t) => expandedRef.current[t.id])
        .forEach((t) => loadSessionData(t));
  };

  /* ---------------------------------------------------------------
   * Finalize / Close / Print  (logica invariata)
   * ------------------------------------------------------------- */
  const finalizeSession = async (table) => {
    const sid = sessionByTable[table.id];
    if (!sid) return alert("Nessuna sessione attiva");
    if (!window.confirm("Finalizzare l’intera sessione?")) return;
    try {
      await fetch(
        `${API_BASE_URL}/table_sessions/${sid}/finalize`,
        { method: "POST", headers: getAuthHeaders() }
      );
      await loadTables();
      alert("Sessione finalizzata con successo!");
    } catch (e) {
      console.error(e);
      alert("Errore durante la finalizzazione");
    }
  };

  const printReceipt = (table) => {
    const sid   = sessionByTable[table.id];
    const items = summaryBySess[sid] || [];
    if (!items.length) return alert("Nessun piatto da stampare");
    const tot = items.reduce((s, d) => s + d.menu_item_price * d.quantity, 0).toFixed(2);
    const w = window.open("", "_blank", "width=600,height=800");
    w.document.write("<h2>Ricevuta</h2>");
    w.document.write(`<h3>Tavolo ${table.table_number}</h3><hr/>`);
    items.forEach((d) =>
      w.document.write(
        `${d.menu_item_name} × ${d.quantity} — € ${(d.menu_item_price * d.quantity).toFixed(2)}<br/>`
      )
    );
    w.document.write(`<hr/><h3>Totale: € ${tot}</h3>`);
    w.print();
    w.close();
  };

  const closeSession = async (table) => {
    if (!window.confirm("Chiudere la sessione del tavolo?")) return;
    try {
      await fetch(
        `${API_BASE_URL}/table_sessions/${sessionByTable[table.id]}`,
        {
          method : "PATCH",
          headers: getAuthHeaders(),
          body   : JSON.stringify({ status: "closed" }),
        }
      );
      await loadTables();
    } catch (e) {
      console.error(e);
      alert("Errore durante la chiusura della sessione");
    }
  };

  /* ---------------------------------------------------------------
   * realtime handlers
   * ------------------------------------------------------------- */
  const applyItemChange = ({ session_id, item }) => {
    setFullItemsBySess((prev) => {
      const oldItems = prev[session_id] || [];
      let newItems;

      if (item.quantity === 0) {
        newItems = oldItems.filter((it) => it.id !== item.id);
      } else {
        const idx = oldItems.findIndex((it) => it.id === item.id);
        if (idx >= 0) {
          newItems = [...oldItems];
          newItems[idx] = { ...newItems[idx], ...item };
        } else {
          newItems = [...oldItems, item];
        }
      }

      const delivered = newItems.filter((it) =>
        ["delivered", "ready", "finalized"].includes(it.status)
      );
      setSummaryBySess((s) => ({ ...s, [session_id]: groupByDish(delivered) }));

      return { ...prev, [session_id]: newItems };
    });
  };

  const handleWsMessage = (raw) => {
    let parsed = null;
    try { parsed = JSON.parse(raw); } catch {/* not JSON */ }

    if (parsed && parsed.type === "order_item_changed") {
      applyItemChange(parsed);
      return;
    }
    if (parsed && parsed.type === "session_closed") {
      const { session_id } = parsed;
      setExpanded((p) => {
        const tbl = sessionToTable[session_id];
        if (!tbl) return p;
        const newExp = { ...p };
        delete newExp[tbl.id];
        return newExp;
      });
      loadTables();
      return;
    }

    if (["reload_tables","reload_orders","reload_kitchen"].includes(raw)) {
      loadTables();
      if (editingSessionRef.current) refreshEditingSession();
      refreshExpandedSessions(tablesRef.current);
    }
  };

  /* ---------------------------------------------------------------
   * effects
   * ------------------------------------------------------------- */
  useEffect(() => { loadTables(); }, [selectedRestaurant]); // eslint-disable-line

  useEffect(() => {
    const ws = new WebSocket(`${WS_BASE_URL}/ws`);
    ws.onmessage = (e) => handleWsMessage(e.data);
    wsRef.current = ws;
    return () => ws.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------------------------------------------------------
   * helpers UI
   * ------------------------------------------------------------- */
  const occupiedTables = tables;                 // tutte già “occupied”
  const totalForTable  = (tableId) => {
    const sid   = sessionByTable[tableId];
    const items = summaryBySess[sid] || [];
    return items.reduce((acc, d) => acc + d.menu_item_price * d.quantity, 0);
  };

  /* ---------------------------------------------------------------
   * UI
   * ------------------------------------------------------------- */
  if (error)             return <div style={st.error}>{error}</div>;
  if (!occupiedTables.length)
    return <div style={st.noData}>Nessun tavolo aperto</div>;

  return (
    <>
      <div style={st.container}>
        <h2 style={st.title}>Tavoli Aperti</h2>

        {occupiedTables.map((t) => {
          const isExp   = expanded[t.id];
          const sid     = sessionByTable[t.id];
          const summary = summaryBySess[sid] || [];
          const done    = finalized[sid];

          return (
            <div style={st.card} key={t.id}>
              <div style={st.cardHeader}>
                <strong>Tavolo {t.table_number}</strong>
                <span>Status: {t.status}</span>
              </div>

              <div style={st.actionsRow}>
                <button
                  style={st.actionBtn}
                  onClick={() => {
                    setExpanded((p) => ({ ...p, [t.id]: !isExp }));
                    if (!isExp) loadSessionData(t);
                  }}
                >
                  {isExp ? "Nascondi" : "Dettaglio"}
                </button>
                <button style={st.closeBtn} onClick={() => closeSession(t)}>
                  Chiudi Sessione
                </button>
              </div>

              {isExp && (
                <div style={st.details}>
                  <h4>Riepilogo piatti consegnati</h4>
                  {summary.length ? (
                    summary.map((d) => (
                      <div key={d.menu_item_id} style={st.dishLine}>
                        {d.menu_item_name} × <strong>{d.quantity}</strong>
                      </div>
                    ))
                  ) : (
                    <p>Nessun piatto consegnato</p>
                  )}

                  <div style={st.totalLine}>
                    Totale: <strong>€ {totalForTable(t.id).toFixed(2)}</strong>
                    {!done && (
                      <button
                        style={st.editBtn}
                        title="Modifica ordine"
                        onClick={() => {
                          setEditingSessionId(sid);
                          setModalOpen(true);
                          loadSessionData(t);          // assicura items freschi
                        }}
                      >
                        📝
                      </button>
                    )}
                  </div>

                  {!done ? (
                    <button
                      style={st.finalizeBtn}
                      onClick={() => finalizeSession(t)}
                    >
                      Finalizza Sessione
                    </button>
                  ) : (
                    <button
                      style={st.printBtn}
                      onClick={() => printReceipt(t)}
                    >
                      Stampa
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ========================= MODAL ========================= */}
      {modalOpen && (
        <div style={st.modalBackdrop} onClick={() => setModalOpen(false)}>
          <div style={st.modal} onClick={(e) => e.stopPropagation()}>
            <h3>Modifica ordine</h3>

            {Object.entries(
              (fullItemsBySess[editingSessionId] || []).reduce((acc, it) => {
                (acc[it.order_id] = acc[it.order_id] || []).push(it);
                return acc;
              }, {})
            ).map(([oid, items]) => (
              <div key={oid} style={st.orderGroup}>
                <h4 style={st.orderHeader}>Ordine #{oid}</h4>
                {items.map((it) => (
                  <div key={it.id} style={st.modalRow}>
                    <span>{it.menu_item_name}</span>
                    <div style={st.modalQty}>
                      <button
                        disabled={saving || it.quantity === 0}
                        onClick={() => handleMinus(it)}
                      >
                        −
                      </button>
                      <span>{it.quantity}</span>
                    </div>
                  </div>
                ))}
              </div>
            ))}

            <button
              style={st.modalClose}
              onClick={() => setModalOpen(false)}
            >
              Chiudi
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------
 * styles (immutati)
 * ---------------------------------------------------------- */
const st = {
  container: { padding: 16 },
  title    : { margin: "0 0 12px", fontSize: 18, fontWeight: "bold" },

  card     : {
    background : "#fff",
    borderRadius: 8,
    padding     : 12,
    marginBottom: 12,
    boxShadow   : "0 2px 5px rgba(0,0,0,0.1)",
  },
  cardHeader: {
    display       : "flex",
    justifyContent: "space-between",
    marginBottom  : 8,
  },

  actionsRow: { display: "flex", gap: 8, marginBottom: 8 },
  actionBtn : {
    background: "#28A745",
    color     : "#fff",
    border    : "none",
    padding   : "8px 12px",
    borderRadius: 6,
    cursor    : "pointer",
    fontWeight: "bold",
  },
  closeBtn: {
    background: "#EB5757",
    color     : "#fff",
    border    : "none",
    padding   : "8px 12px",
    borderRadius: 6,
    cursor    : "pointer",
    fontWeight: "bold",
  },

  details  : {
    background : "#F9F9F9",
    borderRadius: 6,
    padding     : 8,
    marginTop   : 6,
  },
  dishLine : { marginBottom: 6 },
  totalLine: { marginTop: 8, textAlign: "right", fontWeight: "bold" },
  editBtn  : {
    marginLeft: 12,
    background: "transparent",
    border    : "none",
    cursor    : "pointer",
    fontSize  : 18,
  },

  finalizeBtn: {
    marginTop : 10,
    background: "#336699",
    color     : "#fff",
    border    : "none",
    borderRadius: 6,
    padding   : "8px 14px",
    cursor    : "pointer",
    fontWeight: "bold",
  },
  printBtn: {
    marginTop : 10,
    background: "#27AE60",
    color     : "#fff",
    border    : "none",
    borderRadius: 6,
    padding   : "8px 14px",
    cursor    : "pointer",
    fontWeight: "bold",
  },

  /* feedback */
  noData: { padding: 16, textAlign: "center", color: "#333" },
  error : { padding: 16, textAlign: "center", color: "red" },

  /* modal */
  modalBackdrop: {
    position  : "fixed",
    inset     : 0,
    background: "rgba(0,0,0,0.4)",
    display   : "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex    : 999,
  },
  modal: {
    background : "#fff",
    borderRadius: 8,
    padding     : 16,
    minWidth    : 320,
    maxHeight   : "80vh",
    overflowY   : "auto",
  },
  orderGroup : { marginBottom: 12 },
  orderHeader: { margin: "0 0 6px", fontSize: 16, fontWeight: "bold" },
  modalRow : {
    display       : "flex",
    justifyContent: "space-between",
    marginBottom  : 8,
  },
  modalQty: {
    display: "flex",
    gap    : 6,
    alignItems: "center",
  },
  modalClose: {
    marginTop : 14,
    background: "#777",
    color     : "#fff",
    border    : "none",
    borderRadius: 6,
    padding   : "6px 12px",
    cursor    : "pointer",
  },
};
